import { cn } from "@pex/ui-web/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import * as React from "react";

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-4xl border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        outline:
          "border-border bg-input/30 hover:bg-input/50 hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        link: "text-primary underline-offset-4 hover:underline",
        // ── Branded website variants ──────────────────────────────────────
        // All colours go through CSS custom property tokens (bg-brand-*, border-brand-*).
        // Do NOT add OKLCH arbitrary values or baked-in hex here.
        primary:
          "bg-brand-charcoal text-white hover:bg-[#333] active:bg-[#111]",
        fancy:
          "relative isolate overflow-hidden bg-brand-charcoal text-white border-brand-orange-dark shadow-[var(--shadow-glow-brand)] hover:shadow-[var(--shadow-glow-brand-strong)] active:shadow-none transition-[border-color,box-shadow] duration-500 ease-out active:duration-150 before:absolute before:inset-0 before:z-[-1] before:bg-[linear-gradient(270deg,#000_20%,var(--brand-orange-dark)_90%,var(--brand-orange)_100%)] before:opacity-0 hover:before:opacity-100 active:before:opacity-100 before:transition-opacity before:duration-500 active:before:duration-150 before:ease-out",
        hybrid:
          "relative isolate overflow-hidden bg-brand-charcoal text-white border-[#444] hover:border-brand-orange active:border-brand-orange transition-[border-color] duration-500 ease-out active:duration-150 before:absolute before:inset-0 before:z-[-1] before:bg-[linear-gradient(270deg,#000_20%,var(--brand-orange)_90%,var(--brand-orange-light)_100%)] before:opacity-0 hover:before:opacity-100 active:before:opacity-100 before:transition-opacity before:duration-500 active:before:duration-150 before:ease-out",
        "secondary-fancy":
          "bg-white text-brand-charcoal border-brand-charcoal hover:border-brand-orange-light hover:shadow-[var(--shadow-glow-brand)] active:border-brand-orange transition-[border-color,box-shadow] duration-300 ease-out",
        "secondary-hybrid":
          "bg-white text-brand-charcoal border-brand-charcoal hover:border-brand-orange-light active:border-brand-orange transition-[border-color] duration-300 ease-out",
        cta: "relative isolate overflow-hidden bg-white text-brand-charcoal border-white hover:border-white active:border-transparent transition-[border-color] duration-500 ease-out before:absolute before:inset-0 before:z-[-1] before:bg-gradient-to-l before:from-white before:via-brand-orange-lighter before:to-brand-orange-light before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-500 before:ease-out after:absolute after:inset-0 after:z-[-1] after:bg-gradient-to-l after:from-brand-orange-light after:to-brand-yellow after:opacity-0 active:after:opacity-100 after:transition-opacity after:duration-300 after:ease-out",
        "cta-secondary":
          "bg-transparent text-white border-white hover:border-brand-peach hover:backdrop-blur-md active:border-brand-yellow transition-[border-color,backdrop-filter] duration-300 ease-out",
        "link-icon":
          "bg-transparent text-brand-charcoal border-0 shadow-none hover:bg-transparent hover:gap-4 active:gap-4 duration-200 ease-out",
      },
      size: {
        default:
          "h-9 gap-1.5 px-3 has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5",
        xs: "h-6 gap-1 px-2.5 text-xs has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1 px-3 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        lg: "h-10 gap-1.5 px-4 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        icon: "size-9",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    // compoundVariants apply after all regular variants, so these win over size px-* values
    compoundVariants: [{ variant: "link-icon", class: "px-0 self-start" }],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
      data-size={size}
      data-slot="button"
      data-variant={variant}
    />
  );
}

export { Button, buttonVariants };
